import React from 'react';
import { motion } from 'framer-motion';

const PlayerPageSkeleton: React.FC = () => {
  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 flex flex-col transition-colors duration-300">
      <div className="max-w-6xl mx-auto px-4 py-8 flex-1 w-full">

        {/* Profile Header - Centered */}
        <div className="text-center mb-16">
          {/* Profile Animation Circle */}
          <motion.div
            className="flex justify-center mb-6"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
          >
            <div className="w-32 h-32 md:w-40 md:h-40 bg-gradient-to-br from-gray-200 to-gray-300 dark:from-gray-700 dark:to-gray-600 rounded-full animate-pulse transition-colors duration-300" />
          </motion.div>

          {/* Player Name */}
          <motion.div
            className="mb-4 px-4 md:px-8"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3, delay: 0.2 }}
          >
            <div className="flex items-center justify-center gap-3 mb-3">
              <div className="h-14 bg-gray-200 dark:bg-gray-700 rounded-lg w-64 animate-pulse transition-colors duration-300" />
            </div>
          </motion.div>

          {/* Badges */}
          <motion.div
            className="flex items-center justify-center gap-2 flex-wrap mb-3"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3, delay: 0.3 }}
          >
            <div className="h-7 bg-gray-200 dark:bg-gray-700 rounded-full w-20 animate-pulse transition-colors duration-300" />
            <div className="h-7 bg-gray-200 dark:bg-gray-700 rounded-full w-24 animate-pulse transition-colors duration-300" />
          </motion.div>

          {/* Favorite Button */}
          <motion.div
            className="flex justify-center mb-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3, delay: 0.4 }}
          >
            <div className="h-9 bg-gray-200 dark:bg-gray-700 rounded-full w-32 animate-pulse transition-colors duration-300" />
          </motion.div>

          {/* Club Name */}
          <motion.div
            className="flex justify-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3, delay: 0.5 }}
          >
            <div className="h-4 bg-gray-100 dark:bg-gray-600 rounded w-40 animate-pulse transition-colors duration-300" />
          </motion.div>
        </div>

        {/* Two Large Cards Side by Side */}
        <div className="max-w-6xl mx-auto mb-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* ELO Rating Card */}
            <motion.div
              className="bg-gradient-to-br from-gray-200 to-gray-300 dark:from-gray-700 dark:to-gray-600 rounded-2xl p-6 md:p-8 shadow-lg h-48 md:h-56 transition-colors duration-300"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <div className="animate-pulse">
                <div className="h-3 bg-white/40 dark:bg-gray-900/40 rounded w-24 mb-4 transition-colors duration-300" />
                <div className="h-16 bg-white/40 dark:bg-gray-900/40 rounded-lg w-40 mb-6 transition-colors duration-300" />
                <div className="h-20 bg-white/30 dark:bg-gray-900/30 rounded-xl transition-colors duration-300" />
              </div>
            </motion.div>

            {/* Classification Progress Card */}
            <motion.div
              className="bg-gradient-to-br from-gray-200 to-gray-300 dark:from-gray-700 dark:to-gray-600 rounded-2xl p-6 md:p-8 shadow-lg h-48 md:h-56 transition-colors duration-300"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <div className="animate-pulse">
                <div className="flex items-center justify-between mb-6">
                  <div className="h-3 bg-white/40 dark:bg-gray-900/40 rounded w-32 transition-colors duration-300" />
                  <div className="h-10 bg-white/40 dark:bg-gray-900/40 rounded w-16 transition-colors duration-300" />
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-20 h-20 bg-white/40 dark:bg-gray-900/40 rounded-xl transition-colors duration-300" />
                  <div className="flex-1 h-5 bg-white/30 dark:bg-gray-900/30 rounded-full transition-colors duration-300" />
                  <div className="w-20 h-20 bg-white/40 dark:bg-gray-900/40 rounded-xl transition-colors duration-300" />
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* ELO History Chart */}
        <motion.div
          className="max-w-6xl mx-auto mb-16"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.6 }}
        >
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm p-6 md:p-8 transition-colors duration-300">
            <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded-lg w-32 mb-6 animate-pulse transition-colors duration-300" />
            <div className="h-80 bg-gray-100 dark:bg-gray-700 rounded-lg animate-pulse transition-colors duration-300" />
          </div>
        </motion.div>

        {/* Performance Stats Grid */}
        <motion.div
          className="max-w-6xl mx-auto mb-16"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.7 }}
        >
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 transition-colors duration-300">
                <div className="h-4 bg-gray-100 dark:bg-gray-600 rounded w-16 mb-3 animate-pulse transition-colors duration-300" />
                <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded-lg w-20 mb-2 animate-pulse transition-colors duration-300" />
                <div className="h-3 bg-gray-100 dark:bg-gray-600 rounded w-12 animate-pulse transition-colors duration-300" />
              </div>
            ))}
          </div>
        </motion.div>

        {/* Loading indicator */}
        <motion.div
          className="text-center mb-8"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3, delay: 0.8 }}
        >
          <div className="inline-flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 transition-colors duration-300">
            <motion.div
              className="w-1.5 h-1.5 bg-[#1C7AD6] dark:bg-[#3b9eff] rounded-full transition-colors duration-300"
              animate={{
                scale: [1, 1.3, 1],
                opacity: [0.5, 1, 0.5],
              }}
              transition={{
                duration: 1.5,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            />
            <span>Loading player profile...</span>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default PlayerPageSkeleton;
